import { useEffect, useState } from 'react';
import { preloadSfx, SFX_COUNT, isSfxReady } from './sfx';
import { loadBattleAssets, battleAssetCount, isBattleAssetsReady } from './battleAssets';
import { formatApiError } from '../../../utils/apiError';

const MIN_LOADING_MS = 3000;

export interface BattlePreload {
  assetsReady: boolean;
  loadProgress: number;
  loadError: boolean;
  phraseIdx: number;
}

export function useBattlePreload(): BattlePreload {
  const [assetsReady, setAssetsReady] = useState(() => isBattleAssetsReady() && isSfxReady());
  const [loadError, setLoadError] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [phraseIdx, setPhraseIdx] = useState(() => Math.floor(Math.random() * 1_000_000));

  useEffect(() => {
    if (assetsReady) return;
    let cancelled = false;
    const startedAt = Date.now();
    const total = SFX_COUNT + battleAssetCount();
    let done = isBattleAssetsReady() ? battleAssetCount() : 0;
    setLoadProgress(total ? done / total : 1);
    const tick = () => { if (!cancelled) { done += 1; setLoadProgress(Math.min(1, done / total)); } };

    Promise.all([preloadSfx(tick), loadBattleAssets(undefined, tick)])
      .then(() => {
        if (!cancelled) setLoadProgress(1);
        const wait = Math.max(0, MIN_LOADING_MS - (Date.now() - startedAt));
        window.setTimeout(() => { if (!cancelled) setAssetsReady(true); }, wait);
      })
      .catch((err) => {
        console.error('Rush Mid: failed to preload battle assets:', formatApiError(err));
        if (!cancelled) setLoadError(true);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (assetsReady) return;
    const id = window.setInterval(() => setPhraseIdx((i) => i + 1 + Math.floor(Math.random() * 3)), 2200);
    return () => window.clearInterval(id);
  }, [assetsReady]);

  return { assetsReady, loadProgress, loadError, phraseIdx };
}
